import {
  useGetOperationReport,
  usePublishOperationReport,
} from '@shared/api/hooks/reports/reports';
import {
  CompanyReportStatusEnum,
  OperationReportTypeEnum,
} from '@shared/api/types';
import Text from '@shared/components/content/text';
import GuideLayout from '@shared/components/guide-layout';
import Loader from '@shared/components/loader';
import { Button } from '@shared/components/ui/button';
import { useToast } from '@shared/components/ui/use-toast';
import sentry from '@shared/services/sentry';
import { parseISO } from 'date-fns';
import { useCallback, useEffect, useState } from 'react';
import { generatePath, useNavigate, useParams } from 'react-router-dom';
import ReportPublishLoader from '../components/report-publish-loader';
import { getReportsPathMap } from '../utils';

interface CreateReportPublishPageProps {
  reportType: OperationReportTypeEnum;
}

export const ASYNC_PUBLISH_RESPONSE_TIME = 500;
export const LOADER_DURATION = 4000;

export default function CreateReportPublishPage({
  reportType,
}: CreateReportPublishPageProps) {
  const { report_uuid: reportUuid } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { mutateAsync: publishReport, isLoading: isPublishing } =
    usePublishOperationReport();

  const [jobId, setJobId] = useState<string>();
  const [publishTime, setPublishTime] = useState<number>();
  const [isSyncPublishing, setIsSyncPublishing] = useState<boolean>(false);
  const { data: reportData } = useGetOperationReport(reportUuid!, {
    polling: true,
    pollingInterval: 1000,
    enabled: !!jobId,
  });

  const viewReportList = useCallback(() => {
    const listUrl = getReportsPathMap()[reportType].list;
    navigate(listUrl, { replace: true });
  }, [navigate, reportType]);

  const viewResults = useCallback(() => {
    const resultsUrl = generatePath(getReportsPathMap()[reportType].report, {
      report_uuid: reportUuid,
    });

    navigate(resultsUrl, { replace: true });
  }, [navigate, reportUuid, reportType]);

  const handleFailedPublish = useCallback(() => {
    toast({
      title: `Error publishing report`,
      description: 'Please try again or get in touch',
      variant: 'destructive',
    });
    const editUrl = generatePath(getReportsPathMap()[reportType].edit!, {
      report_uuid: reportUuid,
    });

    navigate(editUrl, { replace: true });
  }, [toast, reportType, reportUuid, navigate]);

  useEffect(() => {
    if (!reportUuid) return;

    const publish = async () => {
      try {
        const startTime = Date.now();
        setPublishTime(startTime);

        // Assume it's being handled sync if response isn't quick
        const syncPublishTimeout = setTimeout(() => {
          setIsSyncPublishing(true);
        }, ASYNC_PUBLISH_RESPONSE_TIME);

        const { jobId } = await publishReport({
          operationReportUuid: reportUuid,
        });

        clearTimeout(syncPublishTimeout);

        if (jobId) {
          setJobId(jobId);
          setIsSyncPublishing(false);
        } else {
          const elapsedTime = Date.now() - startTime;
          const remainingTime = Math.max(LOADER_DURATION - elapsedTime, 0);

          setTimeout(() => {
            viewResults();
          }, remainingTime);
        }
      } catch (error) {
        sentry.log(error);
        handleFailedPublish();
      }
    };
    publish();
  }, [
    reportUuid,
    publishReport,
    reportType,
    toast,
    viewResults,
    handleFailedPublish,
  ]);

  // Redirect once async publish is complete
  useEffect(() => {
    if (!jobId || !reportData?.lastPublishedAt || !publishTime) return;

    const newlyPublished =
      parseISO(reportData.lastPublishedAt).getTime() > publishTime;
    if (!newlyPublished) return;

    if (reportData.status === CompanyReportStatusEnum.published) {
      viewResults();
    } else if (reportData.status === CompanyReportStatusEnum.failed) {
      handleFailedPublish();
    }
  }, [handleFailedPublish, jobId, publishTime, reportData, viewResults]);

  const showInitialLoader = isPublishing && !isSyncPublishing && !jobId;
  const showSyncLoader = isSyncPublishing && !jobId;
  const showAsyncLoader = !!jobId;

  return (
    <GuideLayout
      pageName={`Publish ${reportType} report`}
      onClosePath={getReportsPathMap()[reportType].list}
      useReferrer={false}
    >
      <div className="flex flex-1 items-center justify-center">
        {showInitialLoader && <Loader size="sm" />}
        {showSyncLoader && <ReportPublishLoader duration={LOADER_DURATION} />}
        {showAsyncLoader && (
          <div className="flex max-w-lg flex-col items-center space-y-8">
            <div className="flex flex-row items-center space-x-4">
              <div>
                <Loader size="sm" />
              </div>
              <Text variant="cardTitle" className="text-primary-700">
                Generating report
              </Text>
            </div>
            <Text className="text-center text-primary-700">
              Due to the size of this report, it may take a few minutes to
              publish. You can wait on this page and be automatically redirected
              once the report is ready, or leave and check in later on the
              progress from the reports page.
            </Text>
            <Button
              type="button"
              variant="secondary"
              size="lg"
              className="bg-transparent hover:bg-transparent"
              onClick={viewReportList}
            >
              View all {reportType} reports
            </Button>
          </div>
        )}
      </div>
    </GuideLayout>
  );
}
